/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useU2000GridStateHandlers } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU2000SearchStateHandlers } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import type { U2000GridStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import type { U2000SearchStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import { useU2000LUStateHandlers } from '../u2000Core/reducer/formReducer/U2000LUHandlers';
import type { U2000LUState } from '../u2000Core/reducer/formReducer/U2000LUState';
import type { U3202InitLU, U3202OrgProviderConfigSearchRow } from './U3202Service';

export type U3202InitLUContext = U2000LUState['personalization'] &
  U3202InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3202ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3202OrgProviderConfigSearchRow>;
  initLU: U3202InitLU;
}

interface U3202ContextProviderProps {
  initLU: U3202InitLU;
  children: ReactNode;
}

const U3202Context = React.createContext<U3202ContextType | undefined>(undefined);

export const U3202ContextProvider: React.FC<U3202ContextProviderProps> = ({ initLU, children }) => {
  const luStateHandlers = useU2000LUStateHandlers({ initLU: initLU as U3202InitLUContext & U2000LUState });
  const searchStateHandlers = useU2000SearchStateHandlers({
    luName: 'u3202',
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const gridStateHandlers = useU2000GridStateHandlers<U3202OrgProviderConfigSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });

  const contextValue = useMemo<U3202ContextType>(
    () => ({
      luStateHandlers,
      searchStateHandlers,
      gridStateHandlers,
      initLU,
    }),
    [gridStateHandlers, searchStateHandlers, luStateHandlers, initLU],
  );

  return <U3202Context.Provider value={contextValue}>{children}</U3202Context.Provider>;
};

export function useU3202Context(): U3202ContextType {
  const context = useContext(U3202Context);
  if (!context) {
    throw new Error('useU3202Context must be used within a U3202ContextProvider');
  }
  return context;
}
